import React, { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup, TextField } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from '../../../MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import MotionVotingControls from '../../../MotionVotingControls';
import { ActivityType, PersuasiveType, QuestionResponse } from 'store/enums/BallotReviewTypes';
import { Motion } from 'store/types/Meeting';
import { VotingRuleType } from 'store/enums/VoteTypes';
import { defaultTinyMCEProps } from 'util/form';
import {
  createImagesUploadHandler,
  excludeAttendee,
  getAttendeeOptions,
  persuasiveTypeLabel,
  meetingSessionToActivity,
  useSafeAttendeeDisplayValue,
} from 'util/meeting';
import { defaultSnackbarErrorProps } from 'util/layout';

const PersuasiveMotion: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [voted, setVoted] = useState(false);
  const [recommendsTechnicalChange, setRecommendsTechnicalChange] = useState<QuestionResponse | undefined>();
  const [type, setType] = useState<PersuasiveType | undefined>();
  const [reason, setReason] = useState('');
  const [by, setBy] = useState('');
  const [second, setSecond] = useState('');
  const [discussion, setDiscussion] = useState('');

  const byAttendees = useMemo(() => excludeAttendee(attendees || [], second), [attendees, second]);
  const secondAttendees = useMemo(() => excludeAttendee(attendees || [], by), [attendees, by]);

  const byAttendeeOptions = useMemo(
    () => getAttendeeOptions(byAttendees || [], meetingSession?.presidingChairId),
    [byAttendees, meetingSession?.presidingChairId]
  );
  const secondAttendeeOptions = useMemo(
    () => getAttendeeOptions(secondAttendees || [], meetingSession?.presidingChairId),
    [secondAttendees, meetingSession?.presidingChairId]
  );

  const safeBy = useSafeAttendeeDisplayValue(by, byAttendees);
  const safeSecond = useSafeAttendeeDisplayValue(second, secondAttendees);

  const currentMotion: Motion = useMemo(() => {
    return {
      id: `persuasive-motion`,
      title: 'Persuasive Check',
      statement: type ? persuasiveTypeLabel[type] : '',
      reason,
      by: safeBy,
      second: safeSecond,
      discussion,
      recommendsTechnicalChange,
    };
  }, [type, reason, safeBy, safeSecond, discussion, recommendsTechnicalChange]);

  const handleClearMotion = useCallback(() => {
    setType(undefined);
    setReason('');
    setBy('');
    setSecond('');
    setDiscussion('');
  }, []);

  const handleRecommendationChange = useCallback(
    (event) => {
      const value = event.target.value as QuestionResponse;
      setRecommendsTechnicalChange(value);

      MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentMotion: {
          ...currentMotion,
          recommendsTechnicalChange: value,
        },
      })
        .then((updatedSession) => {
          logActivity?.({
            ...meetingSessionToActivity(meetingSession),
            type: ActivityType.Motion,
            motion: updatedSession.currentMotion,
          });

          enqueueSnackbar(`Recommendation displayed`, { variant: 'success' });
        })
        .catch((error) => {
          enqueueSnackbar(`Failed to display recommendation`, defaultSnackbarErrorProps);
          console.error('Error displaying recommendation:', error);
        });
    },
    [currentMotion, enqueueSnackbar, logActivity, meetingId, meetingSession]
  );

  return (
    <GridContainer>
      <GridItem>
        <FormControl>
          <FormLabel>Motion and Reason</FormLabel>
          <RadioGroup
            name="related-type-radio-group"
            value={type ?? ''}
            onChange={(event) => setType(event.target.value as PersuasiveType)}
          >
            <FormControlLabel
              value={PersuasiveType.Persuasive}
              control={<Radio />}
              label={persuasiveTypeLabel[PersuasiveType.Persuasive]}
            />
            <FormControlLabel
              value={PersuasiveType.NotPersuasive}
              control={<Radio />}
              label={`${persuasiveTypeLabel[PersuasiveType.NotPersuasive]} *`}
            />
          </RadioGroup>
        </FormControl>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>Reason *</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id="persuasive-reason-editor"
            value={reason}
            onEditorChange={(newValue: string) => setReason(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'By'}
          select
          required
          value={safeBy}
          onChange={(event) => {
            setBy(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {byAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'Second'}
          select
          required
          value={safeSecond}
          onChange={(event) => {
            setSecond(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {secondAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>Discussion</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id="persuasive-discussion-editor"
            value={discussion}
            onEditorChange={(newValue: string) => setDiscussion(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <MotionVotingControls
          currentMotion={currentMotion}
          requiredFields={
            type === PersuasiveType.NotPersuasive
              ? ['statement', 'reason', 'by', 'second']
              : ['statement', 'by', 'second']
          }
          defaultVotingRule={
            type === PersuasiveType.Persuasive ? VotingRuleType.OneThirdMajority : VotingRuleType.TwoThirdsMajority
          }
          showVotingRuleSelector={false}
          onClearMotion={handleClearMotion}
          onCalculateVotes={() => {
            setRecommendsTechnicalChange(undefined);
            setVoted(true);
          }}
        />
      </GridItem>
      {voted && (
        <GridItem>
          <FormControl>
            <FormLabel>Is a technical change recommended?</FormLabel>
            <RadioGroup
              name="technical-change-radio-group"
              value={recommendsTechnicalChange ?? ''}
              onChange={handleRecommendationChange}
            >
              <FormControlLabel value={QuestionResponse.Yes} control={<Radio />} label={QuestionResponse.Yes} />
              <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={QuestionResponse.No} />
            </RadioGroup>
          </FormControl>
        </GridItem>
      )}
    </GridContainer>
  );
};

export default PersuasiveMotion;
